import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { NativeAppBridgeService, NativeAppMessage } from '@services/native-app-bridge.service'

export type BiometryType = 'fingerprint' | 'face' | 'iris' | 'multiple' | 'none'

export interface BiometricStatus {
	/** Há biometria cadastrada no aparelho e a casca sabe usá-la. */
	available: boolean
	biometryType: BiometryType
	/** O usuário ligou o desbloqueio neste aparelho. */
	enabled: boolean
	/** O usuário já respondeu à oferta (ligou ou recusou): ela não se repete. */
	decided: boolean
	/** Sem casca que responda (app antigo, ou fora do app). */
	unsupported?: boolean
}

/** Estado da cobertura de bloqueio na volta do segundo plano. */
export type AppLockState = 'unlocked' | 'locked' | 'failed'

/** A verificação abre o diálogo do sistema e espera o usuário. */
const VERIFY_TIMEOUT_MS = 120_000

const UNSUPPORTED: BiometricStatus = { available: false, biometryType: 'none', enabled: false, decided: true, unsupported: true }

/**
 * Desbloqueio do app por biometria (spec 058). É um bloqueio de tela: a sessão persiste pelo cookie e não depende dele.
 * A casca guarda a preferência por usuário e faz a verificação; aqui ficam a oferta, o controle de Minha Conta e a
 * cobertura de tela que a casca pede ao voltar do segundo plano.
 */
@Injectable({
	providedIn: 'root'
})
export class NativeBiometricService {
	private readonly lockStateSubject = new BehaviorSubject<AppLockState>('unlocked')

	constructor(private bridge: NativeAppBridgeService) {
		this.bridge.messages$.subscribe(message => this.onNativeMessage(message))
	}

	get available(): boolean {
		return this.bridge.available
	}

	get lockState$(): Observable<AppLockState> {
		return this.lockStateSubject.asObservable()
	}

	async status(username: string): Promise<BiometricStatus> {
		try {
			const result = await this.bridge.request('biometricStatus', { username })

			return {
				available: result['available'] === true,
				biometryType: (result['biometryType'] as BiometryType) ?? 'none',
				enabled: result['enabled'] === true,
				decided: result['decided'] === true
			}
		} catch {
			return { ...UNSUPPORTED }
		}
	}

	/** Liga para o usuário. A casca verifica a biometria antes: resolve `false` se ela não for confirmada. */
	async enable(username: string): Promise<boolean> {
		try {
			const result = await this.bridge.request('biometricEnable', { username }, { timeoutMs: VERIFY_TIMEOUT_MS })
			return result['enabled'] === true
		} catch {
			return false
		}
	}

	async disable(username: string): Promise<void> {
		try {
			await this.bridge.request('biometricDisable', { username })
		} catch {
			// Sem resposta da casca não há o que desligar.
		}
	}

	/** "Agora não": a oferta não volta para este usuário neste aparelho. */
	decline(username: string): void {
		this.bridge.send({ type: 'biometricDecline', username })
	}

	/** Nova tentativa a partir da cobertura de bloqueio. */
	async unlock(): Promise<boolean> {
		this.lockStateSubject.next('locked')

		try {
			const result = await this.bridge.request('biometricUnlock', {}, { timeoutMs: VERIFY_TIMEOUT_MS })
			const success = result['success'] === true

			this.lockStateSubject.next(success ? 'unlocked' : 'failed')
			return success
		} catch {
			this.lockStateSubject.next('failed')
			return false
		}
	}

	/** Tira a cobertura sem verificar: o usuário escolheu entrar com senha e a sessão foi encerrada. */
	release(): void {
		this.lockStateSubject.next('unlocked')
	}

	/**
	 * Oferta única depois do login por formulário: só com biometria disponível e sem resposta anterior do usuário.
	 * `ask` mostra a pergunta e resolve com a escolha. Resolve `true` quando o desbloqueio foi ligado.
	 */
	async offerAfterLogin(username: string | null, ask: () => Promise<boolean>): Promise<boolean> {
		if (!username || !this.available) {
			return false
		}

		const current = await this.status(username)

		if (!current.available || current.decided) {
			return false
		}

		if (!(await ask())) {
			this.decline(username)
			return false
		}

		return this.enable(username)
	}

	private onNativeMessage(message: NativeAppMessage) {
		switch (message.type) {
			case 'appLock':
				this.lockStateSubject.next('locked')
				break
			case 'appUnlock':
				this.lockStateSubject.next('unlocked')
				break
			case 'appLockFailed':
				this.lockStateSubject.next('failed')
				break
		}
	}
}

/** Nome da biometria para os textos da tela, pelo tipo que a casca informa. */
export function biometryLabel(type: BiometryType | undefined): string {
	switch (type) {
		case 'face':
			return 'reconhecimento facial'
		case 'fingerprint':
			return 'digital'
		default:
			return 'biometria'
	}
}
